import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
// ============================================================================
// MANNIS PRO — Full Toolkit
// DNA Builder · Prompt Compiler · Step Sequencer (MIDI/JSON export) · Structure
// Dark / Gold visual identity
// ============================================================================
const GOLD = "#d4af37";
const GOLD_DIM = "#8a7320";
const INK = "#0a0e1a";
const PANEL = "#12172a";
const PANEL_2 = "#0d1120";
const LINE = "#242c44";
const TEXT = "#e8e6df";
const MUTED = "#8b93ad";
// ---------------------------------------------------------------------------
// DNA reference data — the six-layer prompt stack
// ---------------------------------------------------------------------------
const LAYERS = [
  {
    key: "genre",
    label: "Genre",
    options: [
      "ATL trap", "West Coast trap", "gospel-trap", "Southern soul",
      "boom bap", "G-funk", "R&B", "funk-pop", "drill", "neo-soul",
    ],
  },
  {
    key: "vocal",
    label: "Vocal",
    options: [
      "raspy male lead", "smooth tenor", "gospel choir stacks", "auto-tuned melodic",
      "spoken-word ad-libs", "soulful female belt", "layered harmonies", "no vocals",
    ],
  },
  {
    key: "instrumentation",
    label: "Instrumentation",
    options: [
      "808 sub-bass", "Rhodes electric piano", "live drums", "vinyl-sampled loops",
      "talkbox synth", "orchestral strings", "muted horns", "warm analog bass",
    ],
  },
  {
    key: "mood",
    label: "Mood / Energy",
    options: [
      "cold and menacing", "triumphant", "melancholy", "laid-back groove",
      "high-energy anthem", "reflective", "gritty street", "warm and nostalgic",
    ],
  },
  {
    key: "structure",
    label: "Structure",
    options: [
      "intro-verse-hook-verse-hook-outro", "hook-first", "verse-heavy",
      "loop-based", "bridge-driven build", "cold abrupt ending",
    ],
  },
  {
    key: "production",
    label: "Production",
    options: [
      "hard-hitting mix", "lo-fi warmth", "spacious reverb", "tight and dry",
      "tape saturation", "modern loud master", "vintage dusty texture",
    ],
  },
];
const PRESETS = {
  "Virbage Street": {
    genre: "ATL trap", vocal: "raspy male lead", instrumentation: "808 sub-bass",
    mood: "gritty street", structure: "cold abrupt ending", production: "hard-hitting mix",
  },
  "Sunday Service": {
    genre: "gospel-trap", vocal: "gospel choir stacks", instrumentation: "Rhodes electric piano",
    mood: "triumphant", structure: "bridge-driven build", production: "spacious reverb",
  },
  "Down South Soul": {
    genre: "Southern soul", vocal: "soulful female belt", instrumentation: "muted horns",
    mood: "warm and nostalgic", structure: "verse-heavy", production: "vintage dusty texture",
  },
  "Coast Funk": {
    genre: "G-funk", vocal: "smooth tenor", instrumentation: "talkbox synth",
    mood: "laid-back groove", structure: "loop-based", production: "tape saturation",
  },
};
// ---------------------------------------------------------------------------
// Sequencer config
// ---------------------------------------------------------------------------
const MAX_STEPS = 32;
// ---------------------------------------------------------------------------
// Ad-lib templates — Virbage Street stacked parentheticals
// ---------------------------------------------------------------------------
const ADLIB_BANKS = {
  Trap: ["yeah", "skrrt", "huh", "gang", "on god", "woo", "aye", "grrah", "no cap", "facts"],
  Gospel: ["hallelujah", "yes lord", "come on", "sing it", "glory", "amen", "oh yeah", "testify"],
  Soul: ["mmm", "oh baby", "listen", "yeah yeah", "come here", "talk to me", "ooh", "now"],
  Street: ["real one", "we up", "trenches", "no games", "on sight", "ten toes", "solid", "big dawg"],
};
const TRACKS = [
  { name: "Kick", note: 36, color: "#d4af37" },
  { name: "Snare", note: 38, color: "#c98f2e" },
  { name: "Clap", note: 39, color: "#b8862f" },
  { name: "Hat", note: 42, color: "#7c8299" },
  { name: "Open Hat", note: 46, color: "#5f6a8a" },
  { name: "808", note: 35, color: "#e0c04a" },
];
// ---------------------------------------------------------------------------
// Web Audio drum synthesis (no samples needed)
// ---------------------------------------------------------------------------
function useAudioEngine() {
  const ctxRef = useRef(null);
  const getCtx = () => {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return ctxRef.current;
  };
  const playVoice = useCallback((name, time) => {
    const ctx = getCtx();
    const t = time ?? ctx.currentTime;
    const master = ctx.createGain();
    master.connect(ctx.destination);
    const env = (g, a, d, peak = 1) => {
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(peak, t + a);
      g.gain.exponentialRampToValueAtTime(0.0001, t + a + d);
    };
    if (name === "Kick") {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.setValueAtTime(150, t);
      o.frequency.exponentialRampToValueAtTime(45, t + 0.12);
      env(g, 0.005, 0.28, 0.9);
      o.connect(g); g.connect(master); o.start(t); o.stop(t + 0.32);
    } else if (name === "808") {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(90, t);
      o.frequency.exponentialRampToValueAtTime(38, t + 0.05);
      env(g, 0.008, 0.6, 0.85);
      o.connect(g); g.connect(master); o.start(t); o.stop(t + 0.7);
    } else if (name === "Snare" || name === "Clap") {
      const noise = ctx.createBufferSource();
      const buf = ctx.createBuffer(1, ctx.sampleRate * 0.2, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      noise.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = name === "Snare" ? 1800 : 1200;
      const g = ctx.createGain();
      env(g, 0.002, name === "Clap" ? 0.09 : 0.14, 0.7);
      noise.connect(bp); bp.connect(g); g.connect(master);
      noise.start(t); noise.stop(t + 0.2);
    } else {
      // hats
      const noise = ctx.createBufferSource();
      const buf = ctx.createBuffer(1, ctx.sampleRate * 0.1, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      noise.buffer = buf;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 7000;
      const g = ctx.createGain();
      env(g, 0.001, name === "Open Hat" ? 0.18 : 0.045, 0.4);
      noise.connect(hp); hp.connect(g); g.connect(master);
      noise.start(t); noise.stop(t + 0.2);
    }
  }, []);
  const resume = useCallback(() => {
    const ctx = getCtx();
    if (ctx.state === "suspended") ctx.resume();
  }, []);
  return { playVoice, resume, getCtx };
}
// ---------------------------------------------------------------------------
// MIDI file builder (Type 0)
// ---------------------------------------------------------------------------
function buildMidi(grid, bpm, steps) {
  const ppq = 480;
  const stepTicks = ppq / 4; // 16th notes
  const bytes = [];
  const push = (...b) => bytes.push(...b);
  const pushStr = (s) => { for (let i = 0; i < s.length; i++) push(s.charCodeAt(i)); };
  const u32 = (n) => [(n >> 24) & 255, (n >> 16) & 255, (n >> 8) & 255, n & 255];
  const u16 = (n) => [(n >> 8) & 255, n & 255];
  const vlq = (n) => {
    const buf = [n & 0x7f];
    n >>= 7;
    while (n > 0) { buf.unshift((n & 0x7f) | 0x80); n >>= 7; }
    return buf;
  };
  // collect events
  const events = [];
  for (let s = 0; s < steps; s++) {
    TRACKS.forEach((tr, ti) => {
      if (grid[ti][s]) {
        events.push({ tick: s * stepTicks, on: true, note: tr.note });
        events.push({ tick: s * stepTicks + stepTicks - 10, on: false, note: tr.note });
      }
    });
  }
  events.sort((a, b) => a.tick - b.tick || (a.on ? 1 : -1));
  const track = [];
  const uspq = Math.round(60000000 / bpm);
  track.push(0x00, 0xff, 0x51, 0x03, (uspq >> 16) & 255, (uspq >> 8) & 255, uspq & 255);
  let last = 0;
  for (const e of events) {
    const delta = e.tick - last;
    last = e.tick;
    track.push(...vlq(delta));
    track.push(e.on ? 0x99 : 0x89, e.note, e.on ? 100 : 0); // channel 10 (drums)
  }
  track.push(0x00, 0xff, 0x2f, 0x00); // end of track
  pushStr("MThd"); push(...u32(6)); push(...u16(0)); push(...u16(1)); push(...u16(ppq));
  pushStr("MTrk"); push(...u32(track.length)); push(...track);
  return new Uint8Array(bytes);
}
function download(filename, data, mime) {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}
// ---------------------------------------------------------------------------
// UI atoms
// ---------------------------------------------------------------------------
const Eyebrow = ({ children }) => (
  <div style={{
    fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
    color: GOLD, fontWeight: 600, marginBottom: 10, fontFamily: "'DM Mono', monospace",
  }}>{children}</div>
);
function Tabs({ active, setActive }) {
  const items = ["DNA Builder", "Prompt", "Sequencer", "Ad-libs", "Structure"];
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {items.map((it) => (
        <button key={it} onClick={() => setActive(it)}
          data-testid={`toolkit-tab-${it.toLowerCase().replace(/[^a-z]+/g, '-')}`}
          style={{
            padding: "10px 18px", background: active === it ? GOLD : "transparent",
            color: active === it ? INK : MUTED, border: `1px solid ${active === it ? GOLD : LINE}`,
            borderRadius: 2, cursor: "pointer", fontWeight: 600, fontSize: 13,
            letterSpacing: "0.03em", transition: "all .15s",
            fontFamily: "'DM Mono', monospace",
          }}>
          {it}
        </button>
      ))}
    </div>
  );
}
// ---------------------------------------------------------------------------
// DNA Builder
// ---------------------------------------------------------------------------
function DNABuilder({ dna, setDna }) {
  const set = (k, v) => setDna((d) => ({ ...d, [k]: v }));
  return (
    <div>
      <Eyebrow>Six-Layer Prompt Stack</Eyebrow>
      <div style={{ display: "grid", gap: 18 }}>
        {LAYERS.map((layer) => (
          <div key={layer.key}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "baseline",
              marginBottom: 8,
            }}>
              <span style={{ fontWeight: 600, color: TEXT, fontSize: 14 }}>{layer.label}</span>
              <span style={{ fontSize: 12, color: GOLD, fontFamily: "'DM Mono', monospace" }}>
                {dna[layer.key] || "—"}
              </span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {layer.options.map((opt) => {
                const on = dna[layer.key] === opt;
                return (
                  <button key={opt} onClick={() => set(layer.key, on ? "" : opt)}
                    data-testid={`toolkit-opt-${layer.key}-${opt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                    style={{
                      padding: "6px 11px", fontSize: 12.5,
                      background: on ? `${GOLD}22` : PANEL_2,
                      color: on ? GOLD : MUTED,
                      border: `1px solid ${on ? GOLD : LINE}`,
                      borderRadius: 2, cursor: "pointer", transition: "all .12s",
                    }}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 26 }}>
        <Eyebrow>Presets</Eyebrow>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {Object.keys(PRESETS).map((p) => (
            <button key={p} onClick={() => setDna({ ...PRESETS[p] })}
              data-testid={`toolkit-preset-${p.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              style={{
                padding: "8px 14px", background: "transparent", color: TEXT,
                border: `1px solid ${GOLD_DIM}`, borderRadius: 2, cursor: "pointer",
                fontSize: 13, fontWeight: 500,
              }}>
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------
// Prompt Compiler
// ---------------------------------------------------------------------------
function compilePrompt(dna) {
  const parts = LAYERS
    .map((l) => dna[l.key])
    .filter(Boolean);
  if (!parts.length) return "";
  const [genre, vocal, instr, mood, structure, prod] = LAYERS.map((l) => dna[l.key] || "");
  const seg = [];
  if (genre) seg.push(genre);
  if (mood) seg.push(mood);
  if (vocal) seg.push(vocal + " vocals");
  if (instr) seg.push(instr);
  if (prod) seg.push(prod);
  let out = seg.join(", ");
  if (structure) out += `. Structure: ${structure}`;
  return out;
}
function PromptView({ dna, onSendToWorkflow }) {
  const prompt = useMemo(() => compilePrompt(dna), [dna]);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div>
      <Eyebrow>Compiled Style Prompt</Eyebrow>
      <div data-testid="toolkit-compiled-prompt" style={{
        background: PANEL_2, border: `1px solid ${LINE}`, borderRadius: 3,
        padding: 20, minHeight: 120, color: prompt ? TEXT : MUTED,
        fontSize: 15.5, lineHeight: 1.65, fontFamily: "'Fraunces', Georgia, serif",
      }}>
        {prompt || "Pick layers in the DNA Builder to compile a prompt for Suno or Udio."}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button onClick={copy} disabled={!prompt}
          data-testid="toolkit-copy-prompt-btn"
          style={{
            padding: "10px 20px", background: prompt ? GOLD : LINE,
            color: prompt ? INK : MUTED, border: "none", borderRadius: 2,
            cursor: prompt ? "pointer" : "default", fontWeight: 700, fontSize: 13,
            letterSpacing: "0.03em",
          }}>
          {copied ? "Copied" : "Copy prompt"}
        </button>
        <button onClick={() => download("sound-dna-prompt.txt", prompt, "text/plain")}
          disabled={!prompt}
          data-testid="toolkit-export-txt-btn"
          style={{
            padding: "10px 20px", background: "transparent",
            color: prompt ? TEXT : MUTED, border: `1px solid ${LINE}`,
            borderRadius: 2, cursor: prompt ? "pointer" : "default", fontSize: 13,
          }}>
          Export .txt
        </button>
        {onSendToWorkflow && (
          <button onClick={() => onSendToWorkflow(prompt, dna)} disabled={!prompt}
            data-testid="toolkit-send-to-workflow-btn"
            style={{
              padding: "10px 20px", background: "transparent",
              color: prompt ? GOLD : MUTED, border: `1px solid ${prompt ? GOLD : LINE}`,
              borderRadius: 2, cursor: prompt ? "pointer" : "default",
              fontWeight: 700, fontSize: 13, letterSpacing: "0.03em",
            }}>
            Send to Workflow →
          </button>
        )}
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------
// Step Sequencer
// ---------------------------------------------------------------------------
function Sequencer({ dna }) {
  const { playVoice, resume, getCtx } = useAudioEngine();
  const [steps, setSteps] = useState(16);
  const [grid, setGrid] = useState(() =>
    TRACKS.map((_, ti) => {
      const row = Array(MAX_STEPS).fill(false);
      if (ti === 0) { row[0] = row[8] = true; }        // kick
      if (ti === 3) { for (let s = 0; s < MAX_STEPS; s += 2) row[s] = true; } // hat
      return row;
    })
  );
  const [playing, setPlaying] = useState(false);
  const [bpm, setBpm] = useState(140);
  const [current, setCurrent] = useState(-1);
  const stepRef = useRef(0);
  const timerRef = useRef(null);
  const gridRef = useRef(grid);
  gridRef.current = grid;
  const bpmRef = useRef(bpm);
  bpmRef.current = bpm;
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const toggle = (ti, s) => {
    setGrid((g) => g.map((row, i) =>
      i === ti ? row.map((v, j) => (j === s ? !v : v)) : row));
  };
  const stop = useCallback(() => {
    setPlaying(false);
    setCurrent(-1);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);
  const start = useCallback(() => {
    resume();
    setPlaying(true);
    stepRef.current = 0;
    const tick = () => {
      const s = stepRef.current;
      const ctx = getCtx();
      TRACKS.forEach((tr, ti) => {
        if (gridRef.current[ti][s]) playVoice(tr.name, ctx.currentTime);
      });
      setCurrent(s);
      stepRef.current = (s + 1) % stepsRef.current;
      const ms = (60 / bpmRef.current / 4) * 1000;
      timerRef.current = setTimeout(tick, ms);
    };
    tick();
  }, [playVoice, resume, getCtx]);
  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);
  const clear = () => setGrid(TRACKS.map(() => Array(MAX_STEPS).fill(false)));
  const exportMidi = () => download("sound-dna-beat.mid", buildMidi(gridRef.current, bpm, stepsRef.current), "audio/midi");
  const exportJson = () => {
    const payload = {
      format: "sound-dna-pattern-v1",
      bpm, steps,
      prompt: compilePrompt(dna),
      dna,
      tracks: TRACKS.map((tr, ti) => ({ name: tr.name, note: tr.note, steps: grid[ti].slice(0, steps) })),
    };
    download("sound-dna-pattern.json", JSON.stringify(payload, null, 2), "application/json");
  };
  return (
    <div>
      <Eyebrow>Step Sequencer</Eyebrow>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <button onClick={playing ? stop : start}
          data-testid="toolkit-seq-play-btn"
          style={{
            padding: "10px 22px", background: playing ? "#c0392b" : GOLD,
            color: playing ? "#fff" : INK, border: "none", borderRadius: 2,
            cursor: "pointer", fontWeight: 700, fontSize: 13, minWidth: 90,
          }}>
          {playing ? "Stop" : "Play"}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: MUTED, fontSize: 12, fontFamily: "'DM Mono', monospace" }}>BPM</span>
          <input type="range" min={70} max={200} value={bpm}
            onChange={(e) => setBpm(+e.target.value)}
            data-testid="toolkit-seq-bpm-slider"
            style={{ accentColor: GOLD, width: 120 }} />
          <span style={{ color: GOLD, fontSize: 14, width: 34, fontFamily: "'DM Mono', monospace" }}>{bpm}</span>
        </div>
        <button onClick={clear}
          data-testid="toolkit-seq-clear-btn"
          style={{ padding: "9px 16px", background: "transparent", color: MUTED, border: `1px solid ${LINE}`, borderRadius: 2, cursor: "pointer", fontSize: 12 }}>
          Clear
        </button>
        <div style={{ display: "flex", gap: 0, marginLeft: "auto" }}>
          {[16, 32].map((n) => (
            <button key={n} onClick={() => setSteps(n)}
              data-testid={`toolkit-seq-steps-${n}`}
              style={{
                padding: "9px 14px", fontSize: 12, cursor: "pointer",
                background: steps === n ? GOLD : "transparent",
                color: steps === n ? INK : MUTED,
                border: `1px solid ${steps === n ? GOLD : LINE}`,
                borderRadius: 2, fontWeight: 600,
                fontFamily: "'DM Mono', monospace",
              }}>
              {n} steps
            </button>
          ))}
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: steps === 32 ? 720 : 560 }}>
          {TRACKS.map((tr, ti) => (
            <div key={tr.name} style={{ display: "flex", alignItems: "center", marginBottom: 5 }}>
              <div style={{
                width: 78, fontSize: 12, color: TEXT, flexShrink: 0,
                fontFamily: "'DM Mono', monospace",
              }}>{tr.name}</div>
              <div style={{ display: "flex", gap: steps === 32 ? 2 : 3 }}>
                {grid[ti].slice(0, steps).map((on, s) => {
                  const beat = Math.floor(s / 4) % 2 === 0;
                  const isNow = current === s;
                  const cell = steps === 32 ? 18 : 26;
                  return (
                    <button key={s} onClick={() => toggle(ti, s)}
                      data-testid={`toolkit-seq-cell-${ti}-${s}`}
                      style={{
                        width: cell, height: cell, borderRadius: 2, cursor: "pointer",
                        border: isNow ? `1px solid ${GOLD}` : `1px solid ${beat ? LINE : "#1a2036"}`,
                        background: on ? tr.color : (beat ? PANEL : PANEL_2),
                        boxShadow: on ? `0 0 8px ${tr.color}66` : "none",
                        transition: "background .08s",
                      }} />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
        <button onClick={exportMidi}
          data-testid="toolkit-export-midi-btn"
          style={{ padding: "10px 20px", background: GOLD, color: INK, border: "none", borderRadius: 2, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
          Export MIDI
        </button>
        <button onClick={exportJson}
          data-testid="toolkit-export-json-btn"
          style={{ padding: "10px 20px", background: "transparent", color: TEXT, border: `1px solid ${LINE}`, borderRadius: 2, cursor: "pointer", fontSize: 13 }}>
          Export JSON
        </button>
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------
// Structure planner
// ---------------------------------------------------------------------------
const SECTION_TYPES = ["Intro", "Verse", "Hook", "Pre-Hook", "Bridge", "Outro"];
function Structure() {
  const [sections, setSections] = useState([
    { type: "Intro", bars: 4 }, { type: "Verse", bars: 16 },
    { type: "Hook", bars: 8 }, { type: "Verse", bars: 16 },
    { type: "Hook", bars: 8 }, { type: "Outro", bars: 4 },
  ]);
  const add = (type) => setSections((s) => [...s, { type, bars: 8 }]);
  const remove = (i) => setSections((s) => s.filter((_, j) => j !== i));
  const totalBars = sections.reduce((a, s) => a + s.bars, 0);
  return (
    <div>
      <Eyebrow>Song Structure</Eyebrow>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {SECTION_TYPES.map((t) => (
          <button key={t} onClick={() => add(t)}
            data-testid={`toolkit-structure-add-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            style={{ padding: "7px 13px", background: PANEL_2, color: MUTED, border: `1px solid ${LINE}`, borderRadius: 2, cursor: "pointer", fontSize: 12.5 }}>
            + {t}
          </button>
        ))}
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        {sections.map((sec, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 12,
            background: PANEL_2, border: `1px solid ${LINE}`, borderRadius: 2, padding: "10px 14px",
          }}>
            <span style={{ color: GOLD, fontFamily: "'DM Mono', monospace", fontSize: 12, width: 24 }}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <span style={{ flex: 1, color: TEXT, fontWeight: 500 }}>{sec.type}</span>
            <div style={{
              flex: `0 0 ${Math.max(6, (sec.bars / 16) * 140)}px`,
              height: 6, background: GOLD, borderRadius: 3, opacity: 0.7,
            }} />
            <span style={{ color: MUTED, fontSize: 12, width: 52, textAlign: "right", fontFamily: "'DM Mono', monospace" }}>
              {sec.bars} bars
            </span>
            <button onClick={() => remove(i)}
              data-testid={`toolkit-structure-remove-${i}`}
              style={{ background: "transparent", color: MUTED, border: "none", cursor: "pointer", fontSize: 16 }}>×</button>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, color: MUTED, fontSize: 13, fontFamily: "'DM Mono', monospace" }}>
        Total: {totalBars} bars · {sections.length} sections
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------
// Ad-libs — Virbage Street stacked parentheticals
// ---------------------------------------------------------------------------
function AdLibs() {
  const [bank, setBank] = useState("Trap");
  const [lines, setLines] = useState([{ lyric: "", adlibs: [] }]);
  const [copied, setCopied] = useState(false);
  const addLine = () => setLines((l) => [...l, { lyric: "", adlibs: [] }]);
  const removeLine = (i) => setLines((l) => l.filter((_, j) => j !== i));
  const setLyric = (i, v) =>
    setLines((l) => l.map((ln, j) => (j === i ? { ...ln, lyric: v } : ln)));
  const toggleAdlib = (i, word) =>
    setLines((l) => l.map((ln, j) => {
      if (j !== i) return ln;
      const has = ln.adlibs.includes(word);
      return { ...ln, adlibs: has ? ln.adlibs.filter((w) => w !== word) : [...ln.adlibs, word] };
    }));
  const render = (ln) => {
    const stack = ln.adlibs.length ? " " + ln.adlibs.map((w) => `(${w})`).join("") : "";
    return (ln.lyric || "…") + stack;
  };
  const fullText = lines.map(render).join("\n");
  const copy = () => {
    navigator.clipboard?.writeText(fullText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div>
      <Eyebrow>Ad-lib Stacker</Eyebrow>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
        {Object.keys(ADLIB_BANKS).map((b) => (
          <button key={b} onClick={() => setBank(b)}
            data-testid={`toolkit-adlib-bank-${b.toLowerCase()}`}
            style={{
              padding: "7px 14px", fontSize: 12.5, cursor: "pointer",
              background: bank === b ? `${GOLD}22` : PANEL_2,
              color: bank === b ? GOLD : MUTED,
              border: `1px solid ${bank === b ? GOLD : LINE}`, borderRadius: 2,
            }}>
            {b}
          </button>
        ))}
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        {lines.map((ln, i) => (
          <div key={i} style={{
            background: PANEL_2, border: `1px solid ${LINE}`, borderRadius: 3, padding: 14,
          }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
              <span style={{ color: GOLD, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <input value={ln.lyric} onChange={(e) => setLyric(i, e.target.value)}
                placeholder="Type a bar…"
                data-testid={`toolkit-adlib-lyric-${i}`}
                style={{
                  flex: 1, background: INK, border: `1px solid ${LINE}`, borderRadius: 2,
                  color: TEXT, padding: "8px 10px", fontSize: 14,
                  fontFamily: "'Inter', sans-serif",
                }} />
              {lines.length > 1 && (
                <button onClick={() => removeLine(i)}
                  data-testid={`toolkit-adlib-remove-${i}`}
                  style={{ background: "transparent", color: MUTED, border: "none", cursor: "pointer", fontSize: 16 }}>×</button>
              )}
            </div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {ADLIB_BANKS[bank].map((word) => {
                const on = ln.adlibs.includes(word);
                return (
                  <button key={word} onClick={() => toggleAdlib(i, word)}
                    data-testid={`toolkit-adlib-word-${i}-${word.replace(/[^a-z0-9]+/gi, '-')}`}
                    style={{
                      padding: "4px 9px", fontSize: 11.5, cursor: "pointer",
                      background: on ? GOLD : "transparent",
                      color: on ? INK : MUTED,
                      border: `1px solid ${on ? GOLD : LINE}`, borderRadius: 2,
                      fontWeight: on ? 600 : 400,
                    }}>
                    ({word})
                  </button>
                );
              })}
            </div>
            {ln.adlibs.length > 0 && (
              <div style={{
                marginTop: 10, color: TEXT, fontSize: 14.5,
                fontFamily: "'Fraunces', Georgia, serif",
              }}>
                {render(ln)}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button onClick={addLine}
          data-testid="toolkit-adlib-add-bar-btn"
          style={{ padding: "9px 16px", background: "transparent", color: TEXT, border: `1px solid ${GOLD_DIM}`, borderRadius: 2, cursor: "pointer", fontSize: 13 }}>
          + Add bar
        </button>
        <button onClick={copy}
          data-testid="toolkit-adlib-copy-all-btn"
          style={{ padding: "9px 18px", background: GOLD, color: INK, border: "none", borderRadius: 2, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
          {copied ? "Copied" : "Copy all"}
        </button>
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
export default function DNAToolkit({ onSendToWorkflow }) {
  const [active, setActive] = useState("DNA Builder");
  const [dna, setDna] = useState({ ...PRESETS["Virbage Street"] });
  return (
    <div data-testid="dna-toolkit" style={{
      minHeight: "100vh", background: INK, color: TEXT,
      fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
      backgroundImage: `radial-gradient(circle at 20% 0%, ${GOLD}0d, transparent 45%)`,
      borderRadius: 12,
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');
        button:focus-visible { outline: 2px solid ${GOLD}; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
      `}</style>
      <div style={{ maxWidth: 880, margin: "0 auto", padding: "48px 24px 80px" }}>
        {/* Hero */}
        <header style={{ marginBottom: 40 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
            <div style={{
              width: 34, height: 34, border: `1.5px solid ${GOLD}`, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: GOLD, fontFamily: "'DM Mono', monospace", fontSize: 15, fontWeight: 500,
            }}>ॐ</div>
            <div style={{ letterSpacing: "0.28em", fontSize: 12, color: GOLD, fontFamily: "'DM Mono', monospace" }}>
              MANNIS PRO
            </div>
          </div>
          <h1 style={{
            fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600,
            fontSize: "clamp(30px, 5vw, 46px)", lineHeight: 1.05, margin: "10px 0 12px",
            color: TEXT, letterSpacing: "-0.01em",
          }}>
            Engineer the <span style={{ color: GOLD }}>sound</span> before you generate it.
          </h1>
          <p style={{ color: MUTED, fontSize: 15.5, maxWidth: 560, lineHeight: 1.6, margin: 0 }}>
            A prompt-translation layer for Suno and Udio. Build the DNA, compile the prompt,
            sketch the beat, and export MIDI or JSON — all in one place.
          </p>
        </header>
        {/* Nav */}
        <div style={{ marginBottom: 28 }}>
          <Tabs active={active} setActive={setActive} />
        </div>
        {/* Panel */}
        <main style={{
          background: PANEL, border: `1px solid ${LINE}`, borderRadius: 4,
          padding: "28px 26px",
        }}>
          {active === "DNA Builder" && <DNABuilder dna={dna} setDna={setDna} />}
          {active === "Prompt" && <PromptView dna={dna} onSendToWorkflow={onSendToWorkflow} />}
          {active === "Sequencer" && <Sequencer dna={dna} />}
          {active === "Ad-libs" && <AdLibs />}
          {active === "Structure" && <Structure />}
        </main>
        <footer style={{
          marginTop: 32, paddingTop: 20, borderTop: `1px solid ${LINE}`,
          color: MUTED, fontSize: 12, display: "flex", justifyContent: "space-between",
          flexWrap: "wrap", gap: 10, fontFamily: "'DM Mono', monospace",
        }}>
          <span>MANNIS Pro · Virbage Street</span>
          <span>Six-layer stack · MIDI export · Web Audio engine</span>
        </footer>
      </div>
    </div>
  );
}
